import store from "src/store"
import { getNewAccessToken } from "src/store/user"
import { addMessage } from "src/store/messages"

import { logMessage } from "src/logging"
import { soundTheOrder } from "src/utils/orderSounds"

/* Same rule as apiClient's base URL, and the same reason — see the note there. Derived from the
   page rather than written down: `wss:` on an https page and `ws:` on http, so a deployment behind
   TLS cannot open an insecure socket the browser would refuse anyway. */
const sameOriginWs = () => {
  if (typeof window === "undefined") return ""
  const protocol = window.location.protocol === "https:" ? "wss:" : "ws:"
  return `${protocol}//${window.location.host}/ws`
}

const wsUrl = sameOriginWs()

/* The access token rides in `Sec-WebSocket-Protocol` (gamekit 0.11.0): `["access", token]`, and the
   server accepts with the subprotocol `access`. In the query string (`?access=`, which the server
   still reads) it was in every access log and every error report's URL. With no token (a refresh
   that failed) nothing is offered: the server refuses the handshake and the reconnect takes over,
   as it did when the query string read `access=undefined`. Exported for the test. */
export const accessProtocols = (accessToken) =>
  typeof accessToken === "string" && accessToken ? ["access", accessToken] : undefined

// Backoff between reconnection attempts. Bounded on purpose: if the game is still unreachable after
// half a minute the problem is not a blip, and a client retrying forever hides that from the player.
const RECONNECT_DELAYS_MS = [1000, 2000, 4000, 8000, 8000]

/* What still goes out over the game socket while the player looks back at a past round (H4/H5):
   the clock (`next_round`, which `RoundHandler`'s timers keep sending for a host; `set_ready` /
   `unset_ready`, the same round's handshake), the chat, the door out for a spectator, and the
   fog-preview switch, which is a setting of the connection rather than an order. Everything else
   is an order about a board the player is not looking at. */
export const SENT_WHILE_LOOKING_BACK = new Set([
  "next_round",
  "set_ready",
  "unset_ready",
  "send_chat_message",
  "stop_spectating",
  "set_fog_preview",
])

const refusedWhileLookingBack = (key, message) => {
  if (key !== "game") return false
  let lookingBack = false
  try {
    lookingBack = store.getState?.()?.game?.history != null
  } catch {
    lookingBack = false
  }
  return lookingBack && !SENT_WHILE_LOOKING_BACK.has(message?.action)
}

class WebSocketManager {
  static instance = null
  connections = {}
  // Keys answered in this tab rather than over the wire — see `useLocalServer`.
  localServers = {}
  // Every action in the game goes over one of these sockets, so a dropped connection has to be
  // recoverable rather than terminal. Keeping the connect arguments is what makes that possible —
  // the callbacks are closures the views built, and re-registering them is the whole reconnection.
  configs = {}
  deliberateCloses = new Set()
  reconnectAttempts = {}
  reconnectTimers = {}

  constructor() {
    /* Coming back to the game.

       The backoff below is bounded on purpose, and on a desktop that is right: a drop there is a
       network event, and a client still retrying after half a minute is hiding a real problem. On
       a phone it is wrong for a reason that has nothing to do with the network — switching apps
       for a minute is routine, the browser closes the socket and throttles timers while the tab is
       hidden, so the five attempts burn down against a tab nobody is looking at. The player comes
       back to a dead session and is told to reload a game they were in the middle of.

       Returning to the foreground is therefore not a retry, it is a fresh start: the attempt count
       goes back to zero and the socket is opened again. Only sockets the app still wants are
       touched — `configs` is dropped by `close()`, so its absence is what says "the caller shut
       this down deliberately".

       It helps the laptop lid too, which does exactly the same thing for the same reason. */
    if (typeof document !== "undefined" && typeof document.addEventListener === "function") {
      document.addEventListener("visibilitychange", () => this.onReturnToForeground())
    }
  }

  onReturnToForeground() {
    if (typeof document !== "undefined" && document.visibilityState !== "visible") return

    Object.keys(this.configs).forEach((key) => {
      if (this.isConnected(key)) return
      // Back to attempt zero, and a fresh timer: whatever the backoff had climbed to was measuring
      // a tab nobody was watching.
      this._cancelReconnect(key)
      this._scheduleReconnect(key)
    })
  }

  static getInstance() {
    if (!WebSocketManager.instance) {
      WebSocketManager.instance = new WebSocketManager()
    }
    return WebSocketManager.instance
  }

  /* A socket that is answered in this tab instead of over the wire (basic training, 2026-09-14).

     The tutorial runs the **real game view** — the real map, the real docks, the real buttons —
     against a scripted game that no server knows about. Every one of those controls sends through
     `send()`, so this is the one seam that has to know: register a handler for a key and it is
     asked to answer instead of a socket being opened, and whatever it replies is delivered to the
     same `onMessageCallback` list the view registered. Nothing above this line can tell the
     difference, which is the point — a tutorial that used its own buttons would teach its own
     buttons.

     Deliberately not a mock of `WebSocket`: the reconnection machinery, the token refresh and the
     "connection lost" toast are all about a wire that is not there, and a fake socket would drag
     every one of them in. A local server has no connection to lose.

     `reply` is handed to the handler rather than the handler returning a value, so one action can
     answer with several messages the way the real consumer does (a game payload, then a round
     digest) and can answer later than it was called. */
  useLocalServer(key, handler) {
    this.localServers[key] = { handler, listeners: [] }
  }

  stopLocalServer(key) {
    delete this.localServers[key]
  }

  isLocal(key) {
    return Boolean(this.localServers[key])
  }

  /* Registers the view's callbacks against a local server and answers as `connect` would, without
     a socket or a token. The game view calls `connect`; that method routes here when a local
     server holds the key. */
  connectLocally(config) {
    const local = this.localServers[config.key]
    local.listeners = config.onMessageCallback || []
    ;(config.onOpenCallback || []).forEach((callback) => callback({ local: true }))
    return Promise.resolve()
  }

  // Synchronous, and it means *usable* rather than *exists*. It used to be `async`, so callers
  // wrote `if (wsClient.isConnected(key))` against a Promise, which is always truthy.
  isConnected(key) {
    // A local server is always up: there is no wire to drop, and every caller asking this is
    // asking "can I send?", which is yes.
    if (this.localServers[key]) return true
    const socket = this.connections[key]
    return !!socket && socket.readyState === WebSocket.OPEN
  }

  async connect(config) {
    const { key, url, onOpenCallback = [], onMessageCallback = [], onErrorCallback = [], onCloseCallback = [] } = config

    if (this.connections[key]) {
      logMessage({ type: "warning", text: `WebSocket [${key}] already connected.` })
      return
    }

    if (this.localServers[key]) return this.connectLocally(config)

    console.log(`🕑 WebSocket [${key}] connecting.`)
    this.configs[key] = config

    // Retrieve a new access token each time we connect to the ws
    const newAccessToken = await store.dispatch(getNewAccessToken())

    return new Promise((resolve, reject) => {
      const socket = new WebSocket(`${wsUrl}/${url}`, accessProtocols(newAccessToken?.payload?.accessToken))

      socket.onopen = (event) => {
        console.log(`✅ WebSocket [${key}] opened.`, event)
        const wasReconnecting = (this.reconnectAttempts[key] ?? 0) > 0
        this.reconnectAttempts[key] = 0
        if (wasReconnecting) {
          store.dispatch(addMessage({ text: "Reconnected.", type: "success" }))
        }
        onOpenCallback.forEach((callback) => callback(event))
        resolve() // ✅ Now the promise resolves only after connection is open
      }

      socket.onmessage = (event) => {
        const data = JSON.parse(event.data)
        console.log(`📨 Message from [${key}]:`, data)
        onMessageCallback.forEach((callback) => callback(data))
      }

      socket.onerror = (err) => {
        logMessage({ type: "error", text: `WebSocket [${key}] error.` })
        onErrorCallback.forEach((callback) => callback(err))
        reject(err) // ❌ Reject on error so caller can handle
      }

      socket.onclose = (event) => {
        console.log(`❌ WebSocket [${key}] closed.`, event)
        onCloseCallback.forEach((callback) => callback(event))
        delete this.connections[key]

        if (this.deliberateCloses.has(key)) {
          this.deliberateCloses.delete(key)
          return
        }
        // Nothing above the socket notices a drop: the view still renders, the buttons still look
        // live, and every action after this silently goes nowhere. Say so, then try to fix it.
        // Once per drop: a failed reconnect attempt closes too, and must not bring the toast back.
        if ((this.reconnectAttempts[key] ?? 0) === 0) {
          store.dispatch(addMessage({ text: "Connection lost — reconnecting…", type: "warning" }))
        }
        this._scheduleReconnect(key)
      }

      this.connections[key] = socket
    })
  }

  /* An order that leaves makes its sound here (`utils/orderSounds`), whichever button or key sent
     it. `silent` is for a send nobody pressed for — an automatic follow-up the client makes itself. */
  send(key, message, { silent = false } = {}) {
    // Looking back at a past round, no order leaves this tab (requirement 35, H4). The panels
    // already hide their hands; this is the backstop for a key binding or a sheet left open.
    if (refusedWhileLookingBack(key, message)) {
      console.warn(`[${key} ws] not sent while looking back at a past round:`, message?.action)
      return false
    }
    const local = this.localServers[key]
    if (local) {
      // Sounded first: basic training answers in this tab, at once, and the round its answer turns
      // would otherwise be heard before the press that turned it.
      if (!silent) soundTheOrder(key, message)
      local.handler(message, (reply) => local.listeners.forEach((callback) => callback(reply)))
      return true
    }

    const socket = this.connections[key]
    if (socket && socket.readyState === WebSocket.OPEN) {
      if (!silent) soundTheOrder(key, message)
      socket.send(JSON.stringify(message))
      return true
    }

    // This used to be a console.warn, which is invisible to anyone not holding devtools open. A
    // player pressing a button that does nothing has no way to tell a dead socket from a rule they
    // broke, and both look like the game ignoring them.
    console.warn(`⚠️ WebSocket [${key}] is not open.`)
    store.dispatch(
      addMessage({ text: "Not connected — that action did not reach the server.", type: "error" })
    )
    this._scheduleReconnect(key)
    return false
  }

  close(key) {
    // A local server outlives the view's own teardown on purpose: the tutorial registers it and
    // the tutorial removes it (`stopLocalServer`), so a re-mount of the game view inside a running
    // tutorial does not leave the buttons talking to nothing.
    if (this.localServers[key]) {
      this.localServers[key].listeners = []
      return
    }

    const socket = this.connections[key]
    this._cancelReconnect(key)
    delete this.configs[key]
    if (socket) {
      // Marked before closing so `onclose` knows this was us, not the network.
      this.deliberateCloses.add(key)
      socket.close()
      delete this.connections[key]
    } else {
      console.warn(`⚠️ WebSocket [${key}] not found.`)
    }
  }

  closeAll() {
    Object.keys(this.connections).forEach((key) => {
      this.close(key)
    })
  }

  _scheduleReconnect(key) {
    const config = this.configs[key]
    // No config means the caller closed this deliberately, or never opened it: nothing to restore.
    if (!config || this.reconnectTimers[key] || this.connections[key]) return

    const attempt = this.reconnectAttempts[key] ?? 0
    if (attempt >= RECONNECT_DELAYS_MS.length) {
      store.dispatch(
        addMessage({ text: "Could not reconnect — reload the page to keep playing.", type: "error" })
      )
      // Not final: `onReturnToForeground` starts the count again when the tab comes back, which is
      // the case this bound was never about.
      return
    }

    this.reconnectTimers[key] = setTimeout(async () => {
      this.reconnectTimers[key] = null
      this.reconnectAttempts[key] = attempt + 1
      try {
        await this.connect(config)
        // `connect` awaits a token refresh, so a `close()` can land while it is in flight —
        // leaving a socket open after the caller deliberately shut this down. Dropping the config
        // is what close() does, so its absence is the signal that this reconnection is unwanted.
        if (!this.configs[key]) {
          this.close(key)
        }
      } catch (error) {
        this._scheduleReconnect(key)
      }
    }, RECONNECT_DELAYS_MS[attempt])
  }

  _cancelReconnect(key) {
    if (this.reconnectTimers[key]) {
      clearTimeout(this.reconnectTimers[key])
      this.reconnectTimers[key] = null
    }
    this.reconnectAttempts[key] = 0
  }
}

export default WebSocketManager.getInstance()
